import { useRouter } from "next/router";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";

import { logSubmissionError } from "@/monitoring/logActivitySubmissionError";
import useAnnotationDelete from "@/view/utils/useAnnotationDelete";
import useAnnotationPost from "@/view/utils/useAnnotationPost";
import useAnnotationsCommentThread from "@/view/utils/useAnnotationsCommentThread";
import useHandleAnnotationActionsRequest from "@/view/utils/useHandleAnnotationActionsRequest";
import { annotationPropType } from "@/view/viewPropTypes";
import { annotationCardCss } from "./AnnotationCard.style";
import AnnotationCardContents from "./AnnotationCardContents";
import AnnotationCommentThread from "./AnnotationCommentThread";

const propTypes = {
  annotation: annotationPropType,
  annotationCardRefs: PropTypes.shape({
    current: PropTypes.object,
  }),
  comments: PropTypes.arrayOf(annotationPropType),
  focusContentBodyAnnotation: PropTypes.func,
  getAnnotations: PropTypes.func,
  highlightedText: PropTypes.arrayOf(
    PropTypes.shape({
      endOffset: PropTypes.number.isRequired,
      identifier: PropTypes.string,
      parentIdentifier: PropTypes.string.isRequired,
      startOffset: PropTypes.number.isRequired,
      text: PropTypes.string.isRequired,
    }),
  ),
  isDraft: PropTypes.bool,
  legacyArticleId: PropTypes.number,
  newlySavedAnnotationId: PropTypes.number,
  setDraftAnnotation: PropTypes.func,
  setNewlySavedAnnotationId: PropTypes.func,
  updateAnnotationForReadPage: PropTypes.func,
  userProperties: PropTypes.object,
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    unique_id: PropTypes.string,
  }),
  contentId: PropTypes.string,
  currentLevel: PropTypes.object,
  assignmentId: PropTypes.string,
};

const AnnotationCard = ({
  annotation,
  annotationCardRefs,
  comments,
  focusContentBodyAnnotation,
  getAnnotations,
  highlightedText,
  isDraft = false,
  legacyArticleId,
  newlySavedAnnotationId,
  setDraftAnnotation,
  setNewlySavedAnnotationId,
  updateAnnotationForReadPage,
  userProperties,
  contentId,
  currentLevel,
  user,
  assignmentId,
}) => {
  const router = useRouter();
  const userId = user?.id;
  const selectedRadioButtonRef = useRef();
  const deleteCommentButtonRef = useRef();

  const { date_shared: dateShared } = annotation;
  const { isTeacher, isStudent } = userProperties;

  const [updatedAnnotation, setUpdatedAnnotation] = useState({ ...annotation });
  const [isEditMode, setIsEditMode] = useState(isDraft);
  const [isExpanded, setIsExpanded] = useState(isDraft);
  const [saveAnnotation] = useAnnotationPost();
  const [deleteAnnotation] = useAnnotationDelete();
  const [handleAnnotationActionsRequest, isActionPending] =
    useHandleAnnotationActionsRequest();
  const isShared = !!dateShared;
  const shouldGetComments = isStudent && !isDraft;
  const commentThreadProps = useAnnotationsCommentThread({
    shouldGetComments,
    comments,
    annotation,
    userId,
    deleteCommentButtonRef,
  });

  // Expand annotation card for newly saved annotations.
  useEffect(() => {
    const shouldExpandLastAnnotation =
      newlySavedAnnotationId && annotation.id === newlySavedAnnotationId;
    if (shouldExpandLastAnnotation) {
      setIsExpanded(true);
      setNewlySavedAnnotationId(null);
    }
  }, [newlySavedAnnotationId]);

  useEffect(() => {
    setUpdatedAnnotation({ ...annotation });
  }, [annotation]);

  useEffect(() => {
    if (isDraft) {
      if (updatedAnnotation?.pen_name !== annotation?.pen_name) {
        setDraftAnnotation(updatedAnnotation);
      }
    }
  }, [updatedAnnotation]);

  const clearDraftAnnotation = () => setDraftAnnotation(null);

  const createSharePayload = () => {
    // If "isShared" is true then "date_shared" should be null in order to unshare
    const dateNow = new Date().toISOString();
    const dateShared = isShared ? null : dateNow;
    return { ...updatedAnnotation, date_shared: dateShared };
  };

  const handleColorChange = (e) => {
    const color = e.target.value;
    setUpdatedAnnotation({ ...updatedAnnotation, pen_name: color });
  };

  const handleTextChange = (e) => {
    setUpdatedAnnotation({ ...updatedAnnotation, text: e.target.value });
  };

  const handleEditExit = (id) => {
    setIsEditMode(false);

    // Delaying focus to allow the card to re-render after
    // updating edit mode state.
    setTimeout(() => {
      annotationCardRefs.current[id]?.focus();
    }, 50);
  };

  const handleCancel = () => {
    if (isDraft) {
      clearDraftAnnotation();
    } else {
      setUpdatedAnnotation(annotation);
      handleEditExit(annotation.id);
    }
  };

  const handleDelete = async () => {
    await handleAnnotationActionsRequest(
      deleteAnnotation,
      annotation.id,
      getAnnotations,
    );
  };

  const handleEdit = () => {
    setIsEditMode(true);
    // Delaying focus on color picker to allow the card to re-render after
    // updating edit mode state
    setTimeout(() => {
      selectedRadioButtonRef?.current?.focus();
    }, 50);
  };

  const handleSubmit = async () => {
    const annotationToSubmit = updateAnnotationForReadPage(updatedAnnotation);

    const { data, error } = await handleAnnotationActionsRequest(
      saveAnnotation,
      annotationToSubmit,
      getAnnotations,
    );

    if (!error) {
      if (isDraft) await clearDraftAnnotation();
      setNewlySavedAnnotationId(data.id);
      handleEditExit(data.id);
    } else {
      logSubmissionError({
        message: "Unable to submit annotation",
        context: {
          contentId,
          currentLevel,
          user: { id: userId, unique_id: user?.unique_id },
          assignmentId,
          url: router.asPath,
          data,
        },
      });
    }
  };

  const handleShare = async () => {
    const sharePayload = createSharePayload();

    await handleAnnotationActionsRequest(
      saveAnnotation,
      sharePayload,
      getAnnotations,
    );
  };

  const annotationActionProps = {
    handleCancel,
    handleColorChange,
    handleDelete,
    handleEdit,
    handleShare,
    handleSubmit,
    handleTextChange,
  };

  return (
    <section
      tabIndex={0}
      ref={(ref) => {
        annotationCardRefs.current[annotation.id] = ref;
      }}
      css={annotationCardCss(isExpanded)}
      aria-label={isDraft ? "Create Annotation" : "Annotation"}
    >
      <AnnotationCardContents
        {...annotationActionProps}
        annotation={annotation}
        focusContentBodyAnnotation={focusContentBodyAnnotation}
        highlightedText={highlightedText}
        isActionPending={isActionPending}
        isDraft={isDraft}
        isEditMode={isEditMode}
        isExpanded={isExpanded}
        isShared={isShared}
        isStudent={isStudent}
        isTeacher={isTeacher}
        numberOfCommentsInThread={commentThreadProps.numberOfCommentsInThread}
        selectedRadioButtonRef={selectedRadioButtonRef}
        setIsExpanded={setIsExpanded}
        updatedAnnotation={updatedAnnotation}
        userId={userId}
      />
      {isExpanded && shouldGetComments ? (
        <AnnotationCommentThread
          annotation={annotation}
          commentThreadProps={commentThreadProps}
          deleteAnnotation={deleteAnnotation}
          deleteCommentButtonRef={deleteCommentButtonRef}
          getAnnotations={getAnnotations}
          handleAnnotationActionsRequest={handleAnnotationActionsRequest}
          isActionPending={isActionPending}
          isDraft={isDraft}
          legacyArticleId={legacyArticleId}
          saveAnnotation={saveAnnotation}
        />
      ) : null}
    </section>
  );
};

AnnotationCard.propTypes = propTypes;

export default AnnotationCard;
